import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { QrCode, ExternalLink } from "lucide-react";

export default function PairingDialog({ open, onOpenChange, onPaired }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "SUPER_ADMIN";
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [newLoc, setNewLoc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && isSuper) {
      api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
    }
  }, [open, isSuper]);

  useEffect(() => {
    if (!open) return;
    if (isSuper && !tenantId) {
      setLocations([]);
      return;
    }
    const q = isSuper ? `?tenant_id=${tenantId}` : "";
    api.get(`/locations${q}`).then((r) => setLocations(r.data)).catch(() => {});
  }, [open, isSuper, tenantId]);

  async function createLocation() {
    if (!newLoc.trim()) return;
    try {
      const q = isSuper ? `?tenant_id=${tenantId}` : "";
      const { data } = await api.post(`/locations${q}`, { name: newLoc.trim() });
      setLocations((l) => [data, ...l]);
      setLocationId(data.id);
      setNewLoc("");
      toast.success("Standort angelegt");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        code: code.trim(),
        display_name: displayName.trim(),
        location_id: locationId || null,
      };
      if (isSuper) payload.tenant_id = tenantId;
      const { data } = await api.post("/devices/pair", payload);
      toast.success("Gerät gekoppelt");
      onPaired?.();
      onOpenChange(false);
      setCode("");
      setDisplayName("");
      navigate(`/devices/${data.device_id}`);
    } catch (e2) {
      toast.error(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover border-border max-w-md" data-testid="pairing-dialog">
        <div className="flex items-center gap-2 mb-1">
          <QrCode className="text-cyan-400" size={18} strokeWidth={1.5} />
          <DialogTitle className="font-display font-bold text-slate-100">Gerät koppeln</DialogTitle>
        </div>
        <p className="text-xs text-slate-300 mb-4 leading-relaxed">
          6-stelligen Code eingeben, den der{" "}
          <a
            href="/simulator"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-400 hover:text-cyan-300"
            data-testid="pairing-open-simulator-link"
          >
            Geräte-Simulator <ExternalLink size={10} className="inline" />
          </a>{" "}
          anzeigt. Der Code ist 15 Minuten gültig und nur einmal verwendbar.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="pairing-form">
          <div>
            <label className="mono-label block mb-1.5" htmlFor="pairing-code">Pairing-Code</label>
            <input
              id="pairing-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
              className="input-dark font-mono text-2xl tracking-[0.5em] text-center text-cyan-300"
              placeholder="——————"
              required
              data-testid="pairing-code-input"
            />
          </div>

          {isSuper && (
            <div>
              <label className="mono-label block mb-1.5" htmlFor="pairing-tenant">Mandant</label>
              <select
                id="pairing-tenant"
                value={tenantId}
                onChange={(e) => {
                  setTenantId(e.target.value);
                  setLocationId("");
                }}
                className="input-dark"
                required
                data-testid="pairing-tenant-select"
              >
                <option value="">— Mandant wählen —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="mono-label block mb-1.5" htmlFor="pairing-location">Standort</label>
            <select
              id="pairing-location"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="input-dark"
              data-testid="pairing-location-select"
            >
              <option value="">— Kein Standort —</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            <div className="flex gap-2 mt-2">
              <input
                value={newLoc}
                onChange={(e) => setNewLoc(e.target.value)}
                className="input-dark flex-1"
                placeholder="Neuer Standort, z. B. Filiale Mitte"
                disabled={isSuper && !tenantId}
                data-testid="pairing-new-location-input"
              />
              <button
                type="button"
                onClick={createLocation}
                className="btn-ghost text-xs whitespace-nowrap"
                disabled={isSuper && !tenantId}
                data-testid="pairing-create-location-button"
              >
                Anlegen
              </button>
            </div>
          </div>

          <div>
            <label className="mono-label block mb-1.5" htmlFor="pairing-display-name">Display-Name</label>
            <input
              id="pairing-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input-dark"
              placeholder="z. B. Eingang links"
              required
              data-testid="pairing-display-name-input"
            />
          </div>

          <button
            type="submit"
            disabled={busy || code.length !== 6 || !displayName.trim() || (isSuper && !tenantId)}
            className="btn-primary w-full"
            data-testid="pairing-submit-button"
          >
            {busy ? "Kopple…" : "Gerät koppeln"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
